import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n";

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const inline = (s) => {
  let out = escapeHtml(s);
  out = out.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Liens externes : nouvel onglet + noopener (SEO / sécurité)
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="font-semibold text-azure hover:underline">$1</a>');
  // Liens internes : maillage SEO, même onglet
  out = out.replace(/\[([^\]]+)\]\((\/[^)\s]*)\)/g,
    '<a href="$2" class="font-semibold text-azure hover:underline">$1</a>');
  return out;
};

export const renderContent = (content) => {
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) {
      blocks.push(
        <ul key={`l${key}`} className="my-5 list-disc space-y-2 pl-6 text-navy/75">
          {list.map((li, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />
          ))}
        </ul>
      );
      list = [];
    }
  };
  content.split("\n").forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) return flush(idx);
    const img = line.match(/^!\[([^\]]*)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)$/);
    if (img) {
      flush(idx);
      blocks.push(
        <figure key={idx} className="my-8">
          <img src={img[2]} alt={img[1]} loading="lazy"
            className="w-full rounded-3xl object-cover shadow-md" />
          {img[1] && <figcaption className="mt-3 text-center text-xs italic text-navy/45">{img[1]}</figcaption>}
        </figure>
      );
    } else if (line.startsWith("### ")) {
      flush(idx);
      blocks.push(<h3 key={idx} className="mt-8 font-serif text-xl font-semibold text-navy">{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      flush(idx);
      blocks.push(<h2 key={idx} className="mt-10 font-serif text-2xl font-semibold text-navy">{line.slice(3)}</h2>);
    } else if (line.startsWith("- ")) {
      list.push(line.slice(2));
    } else {
      flush(idx);
      blocks.push(
        <p key={idx} className="my-5 leading-relaxed text-navy/75" dangerouslySetInnerHTML={{ __html: inline(line) }} />
      );
    }
  });
  flush("end");
  return blocks;
};

export default function Article() {
  const { slug } = useParams();
  const { t } = useLang();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [others, setOthers] = useState([]);

  useEffect(() => {
    setA(null);
    setNotFound(false);
    api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setNotFound(true));
    api.get("/articles?limit=10")
      .then((r) => setOthers(r.data.filter((x) => x.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  return (
    <div className="min-h-screen bg-sand" data-testid="article-page">
      {a && (
        <Seo
          title={`${a.title} — CabNow`}
          description={a.excerpt || a.title}
          path={`/blog/${a.slug}`}
          jsonLd={{
            "@context": "https://schema.org", "@type": "Article",
            headline: a.title, description: a.excerpt,
            datePublished: a.created_at, inLanguage: a.lang,
            author: { "@type": "Organization", name: "CabNow" },
            ...(a.cover_url && { image: a.cover_url }),
          }}
        />
      )}
      <Navbar />

      <main className="mx-auto max-w-3xl px-4 pb-24 pt-36 sm:px-6">
        <Link to="/blog" data-testid="article-back-link" className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-navy/60 transition-colors hover:text-azure">
          <ArrowLeft size={15} /> {t("blog.back")}
        </Link>

        {!a && !notFound && (
          <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-azure" /></div>
        )}
        {notFound && (
          <div className="rounded-3xl bg-white p-12 text-center shadow-xl" data-testid="article-notfound">
            <h1 className="font-serif text-2xl font-semibold text-navy">{t("confirmation.notfound_title")}</h1>
          </div>
        )}

        {a && (
          <Reveal>
            <article data-testid="article-content">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy/45">
                <Calendar size={12} className="text-gold" /> {a.lang === "en"
                  ? `Published on ${(a.created_at || "").slice(0, 10)}`
                  : t("blog.published_on", { date: (a.created_at || "").slice(0, 10) })}
              </p>
              <h1 className="mt-3 font-serif text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
                {a.title}
              </h1>
              {a.excerpt && <p className="mt-5 text-lg leading-relaxed text-navy/60">{a.excerpt}</p>}
              {a.cover_url && (
                <img src={a.cover_url} alt={a.title} className="mt-8 w-full rounded-3xl object-cover shadow-lg" />
              )}
              <div className="mt-8 border-t border-slate-200 pt-4">{renderContent(a.content)}</div>

              <div className="mt-14 rounded-3xl bg-navy p-8 text-center sm:p-10">
                <h2 className="font-serif text-2xl font-semibold text-white">{t("cta.title")}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-white/60">{t("cta.text")}</p>
                <Link
                  to="/reservation"
                  data-testid="article-book-button"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-azure px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-azure-hover"
                >
                  {t("cta.book")} <ArrowRight size={15} />
                </Link>
              </div>

              {others.length > 0 && (
                <section className="mt-14 border-t border-slate-200 pt-10" data-testid="article-related">
                  <h2 className="font-serif text-2xl font-semibold text-navy">{a.lang === "en" ? "Read next" : t("blog.related")}</h2>
                  <ul className="mt-5 space-y-4">
                    {others.map((o) => (
                      <li key={o.id} data-testid={`article-related-${o.slug}`}>
                        <Link to={`/blog/${o.slug}`} className="font-serif text-lg font-semibold text-azure hover:underline">
                          {o.title}
                        </Link>
                        {o.excerpt && <p className="mt-1 text-sm leading-relaxed text-navy/50">{o.excerpt}</p>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </article>
          </Reveal>
        )}
      </main>

      <Footer />
    </div>
  );
}
